// Story 8.15 (ADO-2024), D-9 and D-10: the caller's undismissed Auto-send outcomes, for the
// banners on Select Depot (every cart) and the cart page (its own cart's failure). Zustand per
// ADR-004.
//
// A store of its own rather than a field of the basket store: the list spans every cart the
// caller orders for, and the basket store holds one cart and resets on every change of cart.
//
// Behaviour:
//   - `load` reads the list. Two consumers mounting together share one request. A failed read
//     keeps what is shown (none on a first failure): the banners are advisory, so a failure
//     shows nothing new rather than an error.
//   - `dismiss` posts the dismissal and drops the outcome once the server agrees (it is then gone
//     for everyone on the cart). A failed dismissal leaves the banner up for another try. A second
//     dismissal of the same outcome while one is in flight is not sent.

import { create } from 'zustand';
import {
  dismissAutoSendOutcome,
  listAutoSendOutcomes,
  type AutoSendOutcome,
} from '../api/auto-send-outcomes.client';

interface AutoSendOutcomesState {
  /** The outcomes from the latest read that landed; `null` before one has. */
  readonly outcomes: readonly AutoSendOutcome[] | null;
  readonly load: () => Promise<void>;
  readonly dismiss: (id: string) => Promise<void>;
}

/** The read in flight, shared by consumers mounting together. */
let inFlight: Promise<void> | null = null;
/** The dismissals in flight, by outcome id. */
const dismissing = new Set<string>();
/** Bumped by every reset: a read or a dismissal that settles after it changes nothing. */
let generation = 0;

export const useAutoSendOutcomesStore = create<AutoSendOutcomesState>((set, get) => ({
  outcomes: null,

  load() {
    if (inFlight !== null) return inFlight;
    const startedIn = generation;
    const read = listAutoSendOutcomes()
      .then(
        (outcomes) => {
          if (startedIn === generation) set({ outcomes });
        },
        () => undefined, // advisory: keep what is shown
      )
      .finally(() => {
        if (inFlight === read) inFlight = null;
      });
    inFlight = read;
    return read;
  },

  async dismiss(id) {
    if (dismissing.has(id)) return;
    dismissing.add(id);
    const startedIn = generation;
    try {
      await dismissAutoSendOutcome(id);
      if (startedIn !== generation) return;
      const outcomes = get().outcomes;
      if (outcomes !== null) set({ outcomes: outcomes.filter((outcome) => outcome.id !== id) });
    } catch {
      // The banner stays up, so the Shopper can dismiss it again.
    } finally {
      dismissing.delete(id);
    }
  },
}));

/** Forget every outcome and drop what is in flight (sign-out; tests, as the suite runs with
 *  `isolate: false`). */
export function resetAutoSendOutcomes(): void {
  generation += 1;
  inFlight = null;
  dismissing.clear();
  useAutoSendOutcomesStore.setState({ outcomes: null });
}
